import React, { useState, useMemo, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { X, Search, ChevronDown, CircleCheck, Ticket, ArrowLeft } from "lucide-react";
import { useTheme } from "../../hooks/useTheme";
import host from "../../global";

const TICKETS_PER_PAGE = 10;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getStatusMeta = (status) => {
  const raw = (status || "").toLowerCase();
  if (["pending", "submitted"].includes(raw))  return { label: "New",         cls: "bg-blue-50   dark:bg-blue-900/20   text-blue-600  dark:text-blue-400   border border-blue-200   dark:border-blue-800"   };
  if (raw.includes("progress"))                return { label: "In Progress", cls: "bg-amber-50  dark:bg-amber-900/20  text-amber-600 dark:text-amber-400  border border-amber-200  dark:border-amber-800"  };
  if (["resolved", "done"].includes(raw))      return { label: "Resolved",    cls: "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800" };
  return { label: raw ? raw.replace(/^./, c => c.toUpperCase()) : "Unknown", cls: "bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700" };
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const ALLOWED_TYPES = ["image/png","image/jpeg","image/jpg","application/pdf","application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel","application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];

const priorities = [
  { label: "High",   color: "text-red-400"   },
  { label: "Medium", color: "text-yellow-400" },
  { label: "Low",    color: "text-green-400"  },
];

// ─── StatusBadge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const { label, cls } = getStatusMeta(status);
  return <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{label}</span>;
};

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) => {
  if (totalPages <= 1) return null;
  const start = (currentPage - 1) * itemsPerPage + 1;
  const end   = Math.min(currentPage * itemsPerPage, totalItems);
  return (
    <div className="flex items-center justify-between mt-5 pt-4 border-t dark:border-gray-700/50 border-gray-200">
      <span className="text-xs dark:text-[#CCCCCCE5] text-textSecondary">Showing {start}–{end} of {totalItems}</span>
      <div className="flex items-center gap-1.5">
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
          className="px-3 py-1.5 text-xs rounded-lg border dark:border-gray-700/50 border-gray-200 dark:text-[#D9D9D9] text-textPrimary disabled:opacity-30 dark:hover:bg-gray-700/50 hover:bg-gray-100 transition-colors">
          ← Prev
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
          <button key={page} onClick={() => onPageChange(page)}
            className={`w-8 h-8 text-xs rounded-lg border transition-all ${
              page === currentPage
                ? "bg-gradient-to-r from-pink-500 to-blue-500 text-white border-transparent shadow"
                : "dark:border-gray-700/50 border-gray-200 dark:text-[#D9D9D9] text-textPrimary dark:hover:bg-gray-700/50 hover:bg-gray-100"
            }`}>
            {page}
          </button>
        ))}
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
          className="px-3 py-1.5 text-xs rounded-lg border dark:border-gray-700/50 border-gray-200 dark:text-[#D9D9D9] text-textPrimary disabled:opacity-30 dark:hover:bg-gray-700/50 hover:bg-gray-100 transition-colors">
          Next →
        </button>
      </div>
    </div>
  );
};

// ─── TicketDetail (accordion body) ────────────────────────────────────────────
const TicketDetail = ({ ticket }) => (
  <div className="px-5 pb-5 pt-3 border-t dark:border-gray-700/30 border-gray-100 space-y-4 dark:bg-white/[0.02] bg-gray-50/60">
    {/* Description */}
    {ticket.description && (
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider dark:text-[#CCCCCCE5] text-textSecondary mb-1.5">Description</p>
        <div className="rounded-xl p-3 text-sm border dark:border-gray-700/30 border-gray-200 dark:bg-gray-800/50 bg-white dark:text-[#D9D9D9] text-textPrimary leading-relaxed">
          {ticket.description}
        </div>
      </div>
    )}

    {/* Remarks timeline */}
    {ticket.remarks?.length > 0 && (
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider dark:text-[#CCCCCCE5] text-textSecondary mb-2">Timeline</p>
        <div className="relative pl-5 space-y-3 border-l-2 dark:border-gray-700 border-gray-200 max-h-[220px] overflow-y-auto pr-1">
          {[...ticket.remarks].reverse().map((r, i) => (
            <div key={i} className="relative">
              <span className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-gradient-to-r from-pink-500 to-blue-500 border-2 dark:border-gray-900 border-white" />
              <p className="text-[10px] dark:text-[#CCCCCCE5] text-textSecondary mb-0.5">{formatDate(r.timestamp)}</p>
              <p className="text-xs dark:text-[#D9D9D9] text-textPrimary">{r.text}</p>
            </div>
          ))}
        </div>
      </div>
    )}

    {!ticket.description && (!ticket.remarks || ticket.remarks.length === 0) && (
      <p className="text-xs dark:text-[#CCCCCCE5] text-textSecondary italic">No additional details available.</p>
    )}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const RaiseTicketPage = () => {
  const { theme } = useTheme();
  const accessToken = sessionStorage.getItem("accessToken");
  const orgAdminId  = localStorage.getItem("org_admin_id");

  // ── Table state ────────────────────────────────────────────────────────────
  const [tickets, setTickets]           = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery]   = useState("");
  const [currentPage, setCurrentPage]   = useState(1);
  const [expandedRow, setExpandedRow]   = useState(new Set());

  // ── Modal state ────────────────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen]                         = useState(false);
  const [ticketTitle, setTicketTitle]                         = useState("");
  const [ticketDescription, setTicketDescription]             = useState("");
  const [ticketTitleError, setTicketTitleError]               = useState("");
  const [ticketDescriptionError, setTicketDescriptionError]   = useState("");
  const [selectedFile, setSelectedFile]                       = useState(null);
  const [priority, setPriority]                               = useState("Medium");
  const [isPriorityOpen, setIsPriorityOpen]                   = useState(false);
  const [isShaking, setIsShaking]                             = useState(false);

  const fileInputRef = useRef(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchTickets = async () => {
    if (!orgAdminId) return;
    try {
      const res = await axios.get(`${host}/get-org-tickets`, {
        params:  { org_admin_id: orgAdminId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setTickets(res.data);
    } catch {
      toast.error("Failed to load tickets.");
    }
  };

  useEffect(() => { fetchTickets(); }, []);

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false); setTicketTitle(""); setTicketDescription("");
    setTicketTitleError(""); setTicketDescriptionError("");
    setSelectedFile(null); setPriority("Medium"); setIsShaking(false);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleBackdropClick = () => {
    if (ticketTitle.trim() || ticketDescription.trim() || selectedFile) {
      setIsShaking(true); setTimeout(() => setIsShaking(false), 500);
    } else closeModal();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (ALLOWED_TYPES.includes(file.type)) { setSelectedFile(file); toast.info(`Selected file: ${file.name}`); }
    else { setSelectedFile(null); fileInputRef.current.value = null; toast.error("Invalid file type. Only PNG, JPG, JPEG, DOC, DOCX, PDF, XLSX, XLS allowed."); }
  };

  const validateFields = () => {
    let valid = true;
    if (!ticketTitle.trim())       { setTicketTitleError("Subject is required");       valid = false; } else setTicketTitleError("");
    if (!ticketDescription.trim()) { setTicketDescriptionError("Description is required"); valid = false; } else setTicketDescriptionError("");
    return valid;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;
    try {
      const response = await axios.post(`${host}/raiseticket`, {}, {
        params:  { title: ticketTitle, description: ticketDescription, org_admin_id: orgAdminId },
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      });
      if (response.status === 200) { toast.success("Ticket Submitted Successfully"); closeModal(); fetchTickets(); }
      else toast.error("Error submitting ticket: " + response.statusText);
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;
        if (status === 401) { sessionStorage.removeItem("accessToken"); toast.error(`Unauthorized: ${data.detail}`); }
        else if (status === 404) toast.warning(`Not Found: ${data.detail}`);
        else toast.error(`Error: ${data.detail}`);
      } else toast.error("An unexpected error occurred. Please try again.");
    }
  };

  const toggleRowExpand = (idx) =>
    setExpandedRow(prev => prev.has(idx) ? new Set() : new Set([idx]));

  // ── Computed data ──────────────────────────────────────────────────────────
  const TAB_FILTERS = useMemo(() => {
    const s = (t) => (t.Status || t.status || "").toLowerCase();
    return [
      { label: "All",         count: tickets.length },
      { label: "New",         count: tickets.filter(t => ["pending","submitted"].includes(s(t))).length },
      { label: "In Progress", count: tickets.filter(t => s(t).includes("progress")).length },
      { label: "Resolved",    count: tickets.filter(t => ["resolved","done"].includes(s(t))).length },
    ];
  }, [tickets]);

  const { paginatedTickets, totalPages, totalItems } = useMemo(() => {
    let filtered = tickets;
    if (statusFilter !== "All") {
      filtered = tickets.filter(t => {
        const s = (t.Status || t.status || "").toLowerCase();
        if (statusFilter === "New")         return ["pending","submitted"].includes(s);
        if (statusFilter === "In Progress") return s.includes("progress");
        if (statusFilter === "Resolved")    return ["resolved","done"].includes(s);
        return false;
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(t => (t.title || "").toLowerCase().includes(q));
    }
    const sorted = [...filtered].sort((a, b) =>
      new Date(b.createdDate || b.CreatedDate) - new Date(a.createdDate || a.CreatedDate)
    );
    const total = sorted.length;
    const pages = Math.ceil(total / TICKETS_PER_PAGE) || 1;
    const start = (currentPage - 1) * TICKETS_PER_PAGE;
    return { paginatedTickets: sorted.slice(start, start + TICKETS_PER_PAGE), totalPages: pages, totalItems: total };
  }, [tickets, statusFilter, searchQuery, currentPage]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-full p-6">
      <style>{`
        @keyframes shake {
          0%,100%{ transform:translateX(0); }
          15%    { transform:translateX(-6px); }
          30%    { transform:translateX(6px); }
          45%    { transform:translateX(-5px); }
          60%    { transform:translateX(5px); }
          75%    { transform:translateX(-3px); }
          90%    { transform:translateX(3px); }
        }
        .shake { animation: shake 0.5s ease; }
      `}</style>

      {/* ── Page header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 flex items-center justify-center rounded-full dark:hover:bg-gray-700/50 hover:bg-gray-100 transition-colors dark:text-[#D9D9D9] text-textPrimary"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-xl dark:text-[#D9D9D9] text-textPrimary">Support Tickets</h1>
        </div>
        <button
          onClick={openModal}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-sm font-semibold hover:opacity-90 active:scale-95 transition-all shadow-sm"
        >
          <span className="text-base leading-none">+</span> Raise New Ticket
        </button>
      </div>

      {/* ── Content card ────────────────────────────────────────────────────── */}
      <div className="border dark:border-gray-700/50 border-gray-200 rounded-xl dark:glass-effect bg-bgPrimary">

        {/* Filter tabs + search */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b dark:border-gray-700/50 border-gray-200">
          {/* Tab strip */}
          <div className="flex gap-1 p-1 rounded-xl border dark:border-gray-700/50 border-gray-200 bg-bgSecondary dark:bg-white/5 w-fit">
            {TAB_FILTERS.map(({ label, count }) => {
              const active = statusFilter === label;
              return (
                <button key={label}
                  onClick={() => { setStatusFilter(label); setCurrentPage(1); setExpandedRow(new Set()); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                    active
                      ? "bg-gradient-to-r from-pink-500 to-blue-500 text-white shadow-sm"
                      : "dark:text-[#D9D9D9] text-textSecondary dark:hover:bg-gray-700/50 hover:bg-gray-100"
                  }`}>
                  {label}
                  <span className={`text-[10px] px-1 rounded-full ${active ? "bg-white/20" : "dark:bg-gray-700/50 bg-gray-200"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              placeholder="Search tickets..."
              className="pl-9 pr-4 py-2 text-xs rounded-full border dark:border-gray-600 border-gray-300 dark:bg-white/5 bg-white dark:text-[#D9D9D9] text-textPrimary placeholder-gray-400 focus:outline-none focus:border-pink-400 transition-colors w-52"
            />
          </div>
        </div>

        {/* Ticket list */}
        <div className="p-4">
          {paginatedTickets.length > 0 ? (
            <div className="space-y-2">
              {paginatedTickets.map((ticket, index) => {
                const globalIdx  = (currentPage - 1) * TICKETS_PER_PAGE + index;
                const isExpanded = expandedRow.has(globalIdx);
                const p          = ticket.priority;
                const leftColor  = p === "High" ? "#ef4444" : p === "Medium" ? "#f59e0b" : "#10b981";

                return (
                  <div key={ticket._id || globalIdx}
                    className="border dark:border-gray-700/50 border-gray-200 rounded-xl overflow-hidden transition-shadow hover:shadow-md dark:hover:shadow-gray-800">

                    {/* Row header */}
                    <div
                      className="flex items-center gap-3 p-4 cursor-pointer dark:hover:bg-white/[0.02] hover:bg-gray-50/70 transition-colors"
                      onClick={() => toggleRowExpand(globalIdx)}
                    >
                      {/* Priority dot */}
                      <span className="w-2.5 h-2.5 rounded-full flex-none" style={{ background: leftColor }} />

                      {/* Title + meta */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold dark:text-[#D9D9D9] text-textPrimary truncate" title={ticket.title}>
                          {ticket.title || "Untitled"}
                        </p>
                        <p className="text-xs dark:text-[#CCCCCCE5] text-textSecondary mt-0.5 truncate">
                          {ticket.orgadminEmail || "—"} · {formatDate(ticket.createdDate || ticket.CreatedDate)}
                        </p>
                      </div>

                      {/* Status badge */}
                      <StatusBadge status={ticket.Status || ticket.status} />

                      {/* Toggle */}
                      <button
                        onClick={e => { e.stopPropagation(); toggleRowExpand(globalIdx); }}
                        className="flex items-center gap-1 text-xs dark:text-[#CCCCCCE5] text-textSecondary hover:text-pink-500 dark:hover:text-pink-400 transition-colors flex-none ml-1"
                      >
                        Details
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
                      </button>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && <TicketDetail ticket={ticket} />}
                  </div>
                );
              })}
            </div>
          ) : (
            /* Empty state */
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #fce7f3, #dbeafe)" }}>
                <Ticket className="w-8 h-8 text-pink-400" />
              </div>
              <p className="text-sm font-semibold dark:text-[#D9D9D9] text-textPrimary">No tickets found</p>
              <p className="text-xs dark:text-[#CCCCCCE5] text-textSecondary text-center max-w-xs">
                {searchQuery.trim()
                  ? `No results for "${searchQuery}"`
                  : statusFilter !== "All"
                    ? `No "${statusFilter}" tickets yet`
                    : "You haven't raised any support tickets yet."}
              </p>
              {statusFilter === "All" && !searchQuery && (
                <button onClick={openModal}
                  className="mt-1 px-5 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-xs font-semibold hover:opacity-90 transition-all">
                  + Raise a Ticket
                </button>
              )}
            </div>
          )}

          <Pagination
            currentPage={currentPage} totalPages={totalPages}
            onPageChange={setCurrentPage} totalItems={totalItems} itemsPerPage={TICKETS_PER_PAGE}
          />
        </div>
      </div>

      {/* ── Raise Ticket Modal (original) ───────────────────────────────────── */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[99999999]">
          <div
            className="absolute inset-0 bg-bgPrimary/50 dark:bg-black/20"
            style={{ backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
            onMouseDown={handleBackdropClick}
          />
          <div className={`w-full max-w-xl p-6 rounded-xl shadow-lg relative bg-bgSecondary ${isShaking ? "shake" : ""}`}
            style={{ background: theme === "dark" ? "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))" : "" }}>

            <div className="flex items-center justify-between">
              <h3 className="dark:text-[#C1A0D0] text-textPrimary text-left text-lg">Raise a Ticket</h3>
              <button onClick={closeModal}><X className="h-5 w-5 dark:text-[#C1A0D0] text-textPrimary" /></button>
            </div>

            <div className="flex items-start space-x-5 mt-10">
              <div className="flex flex-col flex-1">
                <span className="dark:text-[#CCCCCC] text-textSecondary text-sm">Subject*</span>
                <input type="text" value={ticketTitle}
                  onChange={e => { setTicketTitle(e.target.value); if (e.target.value.trim()) setTicketTitleError(""); }}
                  onBlur={() => { if (!ticketTitle.trim()) setTicketTitleError("Subject is required"); }}
                  className={`w-full px-3 py-2 bg-transparent border ${ticketTitleError ? "border-red-500" : "dark:border-gray-700/50 border-gray-300"} rounded dark:text-[#CCCCCC] text-textSecondary placeholder-gray-400 mt-2 focus:outline-none`}
                  placeholder="Enter a subject"
                />
                {ticketTitleError && <span className="text-red-400 text-xs mt-1">{ticketTitleError}</span>}
              </div>

              <div className="flex flex-col">
                <span className="dark:text-[#CCCCCC] text-textSecondary text-sm">Priority*</span>
                <div className="relative">
                  <button onClick={() => setIsPriorityOpen(!isPriorityOpen)}
                    className="w-36 px-3 py-2 bg-transparent border dark:border-slate-600 border-gray-300 rounded flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-sm ${priority === "High" ? "bg-red-500" : priority === "Medium" ? "bg-yellow-500" : "bg-green-500"}`} />
                      <span className={priority === "High" ? "text-red-500" : priority === "Medium" ? "text-yellow-500" : "text-green-500"}>{priority}</span>
                    </div>
                    <ChevronDown size={16} className={`text-[#C9A3FF] transition-transform ${isPriorityOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isPriorityOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 dark:bg-slate-700 border dark:border-slate-600 bg-bgPrimary border-gray-400 rounded shadow-lg z-10">
                      {priorities.map(item => (
                        <button key={item.label} onClick={() => { setPriority(item.label); setIsPriorityOpen(false); }}
                          className="w-full px-3 py-2 text-left dark:hover:bg-slate-600 hover:bg-gray-200 flex items-center gap-2 transition-colors">
                          <div className={`w-2 h-2 rounded-sm ${item.label === "High" ? "bg-red-500" : item.label === "Medium" ? "bg-yellow-500" : "bg-green-500"}`} />
                          <span className={item.color}>{item.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col mt-5">
              <span className="dark:text-[#CCCCCC] text-textSecondary text-sm">Description*</span>
              <textarea value={ticketDescription}
                onChange={e => { setTicketDescription(e.target.value); if (e.target.value.trim()) setTicketDescriptionError(""); }}
                onBlur={() => { if (!ticketDescription.trim()) setTicketDescriptionError("Description is required"); }}
                className={`w-full px-3 py-3 bg-transparent border ${ticketDescriptionError ? "border-red-500" : "dark:border-gray-700/50 border-gray-300"} rounded dark:text-[#CCCCCC] text-textSecondary placeholder-gray-400 mt-2 focus:outline-none`}
                rows="6" placeholder="Describe your issue..."
              />
              {ticketDescriptionError && <span className="text-red-400 text-xs mt-1">{ticketDescriptionError}</span>}
            </div>

            <div className="mt-5">
              <span className="dark:text-[#CCCCCC] text-textSecondary text-sm">Attach File</span>
              <div className="flex items-center space-x-5 py-1">
                <button onClick={() => fileInputRef.current.click()}
                  className="dark:text-[#CCCCCC] text-textSecondary bg-[#FFFFFF33] px-2 py-2 border dark:border-gray-700/50 border-gray-300 rounded-full w-48 text-sm overflow-hidden text-ellipsis whitespace-nowrap">
                  {selectedFile ? selectedFile.name : "Choose File"}
                </button>
                <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".png,.jpg,.jpeg,.doc,.docx,.pdf,.xlsx,.xls" className="hidden" />
                {selectedFile === null
                  ? <span className="dark:text-[#A69393] text-textSecondary text-xs">Only png/jpg/jpeg/doc/docx/pdf/xlsx/xls files are allowed to upload.</span>
                  : <div className="flex items-center space-x-4"><CircleCheck className="w-5 h-5 text-green-400" /><span className="dark:text-[#A69393] text-textSecondary text-xs">File Uploaded</span></div>
                }
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button onClick={handleSubmit} className="px-8 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-sm">
                Create a Ticket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RaiseTicketPage;
